import { Box, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fromLonLat } from 'ol/proj';
import Overlay from 'ol/Overlay';
import { unByKey } from 'ol/Observable';
import Map from 'ol/Map';
import View from 'ol/View';
import { Attribution, Zoom } from 'ol/control';
import WebGLTile from 'ol/layer/WebGLTile';
import 'ol/ol.css';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { MAPBOX_STYLE_TILE } from '../constants/mapbox';
import SliderBox from './SliderBox';
import { latlngType } from '../types';
import VectorSource from 'ol/source/Vector';
import tBlue from '../../../assets/t_blue.png';
import tOrange from '../../../assets/t_orange.png';
import pin from '../../../assets/pin.png';
import current from '../../../assets/current.png';
import mapboxLogo from '../../../assets/mapbox-logo.svg';

import VectorLayer from 'ol/layer/Vector';
import {
  drawAreaFromAlert,
  drawPinFromLonlat,
  drawPointFromAlert,
} from '../functions/drawAreasFromAlerts';
import { alertListType } from 'features/alerts/types';
import { colorStyle } from '../constants/colorStyle';
import { Station } from '../api/getStations';
import { Feature, MapBrowserEvent } from 'ol';
import Cluster from 'ol/source/Cluster';
import { Point } from 'ol/geom';
import { Icon, Style, Text, Fill, Stroke } from 'ol/style';
import type { FeatureLike } from 'ol/Feature';
import { useIsMobile } from '../../../hooks/useIsMobile';
import FloodDepthMeter from './FloodDepthMeter';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import TimeSelectSlider from './TimeSelectSlider';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';


const MAPBOX_LAYER_TYPE = {
  default: 'default',
  dark: 'dark',
  satellite: 'satellite',
};

const MAPBOX_LAYER_TYPE_SELECT_MENUITEMS = [
  { label: 'common.label.default', value: MAPBOX_LAYER_TYPE.default },
  { label: 'common.label.dark', value: MAPBOX_LAYER_TYPE.dark },
  { label: 'common.label.satellite', value: MAPBOX_LAYER_TYPE.satellite },
];

const LOCALE_TYPE = {
  ja: 'ja',
  en: 'en',
};

const MAPBOX_LAYERS = [
  {
    lang: LOCALE_TYPE.ja,
    type: MAPBOX_LAYER_TYPE.default,
    url: MAPBOX_STYLE_TILE.ja.default,
  },
  {
    lang: LOCALE_TYPE.ja,
    type: MAPBOX_LAYER_TYPE.dark,
    url: MAPBOX_STYLE_TILE.ja.dark,
  },
  {
    lang: LOCALE_TYPE.ja,
    type: MAPBOX_LAYER_TYPE.satellite,
    url: MAPBOX_STYLE_TILE.ja.satellite,
  },
  {
    lang: LOCALE_TYPE.en,
    type: MAPBOX_LAYER_TYPE.default,
    url: MAPBOX_STYLE_TILE.en.default,
  },
  {
    lang: LOCALE_TYPE.en,
    type: MAPBOX_LAYER_TYPE.dark,
    url: MAPBOX_STYLE_TILE.en.dark,
  },
  {
    lang: LOCALE_TYPE.en,
    type: MAPBOX_LAYER_TYPE.satellite,
    url: MAPBOX_STYLE_TILE.en.satellite,
  },
];

// Mapboxの帰属表示。ロゴ・文字とも地図上に常に見えている必要があり、折りたたみは不可
// ロゴは Mapbox の指定どおり 65x20px で出し、見た目は変えない
// https://docs.mapbox.com/help/getting-started/attribution/
const MAPBOX_ATTRIBUTIONS = [
  `<a href="https://www.mapbox.com/" target="_blank" rel="noopener noreferrer"><img class="mapbox-logo" src="${mapboxLogo}" alt="Mapbox" width="65" height="20"></a>`,
  '<a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noopener noreferrer">© Mapbox</a>',
  '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap</a>',
  '<a href="https://apps.mapbox.com/feedback/" target="_blank" rel="noopener noreferrer">Improve this map</a>',
];

// 航空写真のスタイルは Maxar の表記も要る
const MAPBOX_SATELLITE_ATTRIBUTIONS = [
  ...MAPBOX_ATTRIBUTIONS,
  '<a href="https://www.maxar.com/" target="_blank" rel="noopener noreferrer">© Maxar</a>',
];

type PredictionMapProps = {
  latlng: latlngType;
  style?: any;
  alerts: alertListType;
  goToCurrentLocation: () => void;
  pins?: Station[];
  onClick?: (index: number) => void;
  increment: number;
  setIncrement: React.Dispatch<React.SetStateAction<number>>;
  isImageIdChecking: boolean;
  showFloodDepthControls?: boolean;
};

export type PredictionMapHandle = {
  // 返り値は新しい浸水深タイルの読み込みが一段落したら resolve する（先読みを始める合図）
  updateGeeUrl: (nextGeeUrl: string) => Promise<void>;
};

const blueIconStyle = new Style({
  image: new Icon({
    src: tBlue,
    scale: 1,
  }),
});

const orangeIconStyle = new Style({
  image: new Icon({
    src: tOrange,
    scale: 1,
  }),
});

const pinIcon = new Style({
  image: new Icon({
    src: pin,
    scale: 1,
  }),
});

const currentIcon = new Style({
  image: new Icon({
    src: current,
    scale: 1,
  }),
});

const normalizeLanguage = (language: string) => (
  language?.startsWith(LOCALE_TYPE.en) ? LOCALE_TYPE.en : LOCALE_TYPE.ja
);

// localStorageの保存値など、想定外の背景種別は通常の地図に寄せる
const normalizeLayerType = (layerType: string | null) => (
  layerType && Object.values(MAPBOX_LAYER_TYPE).includes(layerType) ? layerType : MAPBOX_LAYER_TYPE.default
);

const findBaseLayerIndex = (language: string, layerType: string) => {
  const found = MAPBOX_LAYERS.findIndex((d) => d.lang === language && d.type === layerType);
  return found >= 0 ? found : 0;
};

const createAlertFeatures = (alerts: alertListType) => {
  const rectangles = alerts
    .filter(alert => !!alert.is_area)
    .map(alert => drawAreaFromAlert(alert));
  const points = alerts.filter(alert => !alert.is_area).map(alert => drawPointFromAlert(alert));
  return [...points, ...rectangles];
};

const createStationFeatures = (pins: Station[]) => pins.map((p: Station, index) => {
  const title = `${p.basin_name || ''} ${p.river_name || ''} ${p.station_name || ''}`;
  return new Feature({
    geometry: new Point(fromLonLat([Number(p.lon), Number(p.lat)])),
    index,
    title,
    station: p,
  });
});

// クラスタのスタイル
const clusterStyle = (feature: FeatureLike) => {
  const features = feature.get('features') as Feature[];
  const size = features.length;
  if (size === 1) {
    return blueIconStyle;
  } else {
    // 複数個クラスタの場合は円＋数字
    return new Style({
      image: new Icon({
        src: tBlue,
        scale: 0.2,
      }),
      text: new Text({
        text: size.toString(),
        scale: 1.2,
        fill: new Fill({ color: '#fff' }),
        stroke: new Stroke({ color: '#1976d2', width: 3 }),
        offsetY: 0,
      }),
    });
  }
};

// 破棄は次の描画のあとにする。外した直後は前フレームのframeStateにまだ残っていて、
// その間のpointermove（forEachFeatureAtPixel）が破棄済みレイヤーを参照して落ちるため
const disposeLayer = (map: Map, layer: WebGLTile) => {
  map.removeLayer(layer);
  map.once('postrender', () => layer.dispose());
};

type MapResources = {
  map: Map;
  baseLayers: TileLayer<XYZ>[];
  alertSource: VectorSource;
  locationSource: VectorSource;
  pinSource: VectorSource;
  selectedPinSource: VectorSource;
  // 検索などコードからの移動をGA4の操作計測に含めない
  skipNextMoveendAnalytics: () => void;
  dispose: () => void;
};

type CreateMapResourcesParams = {
  target: HTMLElement;
  popup: HTMLElement;
  initialLatlng: latlngType;
  initialBaseLayerIndex: number;
  onStationClick: (index: number) => void;
  onStationHover: (station: Station | null) => void;
};

// Map本体と各sourceを1度だけ作る。変化するpropsはここで参照せず、各effectで反映する
const createMapResources = ({
  target,
  popup,
  initialLatlng,
  initialBaseLayerIndex,
  onStationClick,
  onStationHover,
}: CreateMapResourcesParams): MapResources => {
  const overlay = new Overlay({
    element: popup,
    autoPan: {
      animation: {
        duration: 150,
      },
    },
  });

  const centerStr = localStorage.getItem('predictionMap.center') || null;
  const zoomStr = localStorage.getItem('predictionMap.zoom') || null;
  const center = initialLatlng
    ? fromLonLat([initialLatlng[1], initialLatlng[0]])
    : centerStr
    ? centerStr.split(',').map(d => Number(d))
    : fromLonLat([139.767221, 35.680885]);
  const zoom = zoomStr ? Number(zoomStr) : 10;

  const alertSource = new VectorSource();
  const locationSource = new VectorSource();
  const pinSource = new VectorSource();
  const selectedPinSource = new VectorSource();

  // pinsクラスタリング
  const clusterSource = new Cluster({
    distance: 40, // ピクセル単位のクラスタ距離
    source: pinSource,
  });

  // 選択中の背景だけをvisibleにする。opacity:0のレイヤーもタイル取得の対象になるため
  const baseLayers = MAPBOX_LAYERS.map(
    (d, i) =>
      new TileLayer({
        source: new XYZ({
          attributions: d.type === MAPBOX_LAYER_TYPE.satellite ? MAPBOX_SATELLITE_ATTRIBUTIONS : MAPBOX_ATTRIBUTIONS,
          url: d.url,
          crossOrigin: 'anonymous',
        }),
        zIndex: 1,
        opacity: 1,
        visible: i === initialBaseLayerIndex,
      }),
  );

  const map = new Map({
    layers: [
      ...baseLayers,
      new VectorLayer({ source: alertSource, zIndex: 3 }),
      new VectorLayer({ source: locationSource, zIndex: 3 }),
      new VectorLayer({ source: clusterSource, style: clusterStyle, zIndex: 3 }),
      new VectorLayer({ source: selectedPinSource, style: orangeIconStyle, zIndex: 3 }),
    ],
    view: new View({
      center,
      zoom,
      maxZoom: undefined,
    }),
    controls: [],
    overlays: [overlay],
  });
  let lastInteractionType = 'programmatic';
  let previousZoom = zoom;
  // 初回描画時のmoveendは計測しない
  let shouldSkipMoveendAnalytics = true;

  const viewport = map.getViewport();
  const handleWheel = () => {
    lastInteractionType = 'wheel_zoom';
  };
  const handleTouchStart = (event: TouchEvent) => {
    if (event.touches.length > 1) {
      lastInteractionType = 'pinch_zoom';
    }
  };
  viewport.addEventListener('wheel', handleWheel, { passive: true });
  viewport.addEventListener('touchstart', handleTouchStart, { passive: true });
  map.setTarget(target);

  map.on('pointerdrag', () => {
    lastInteractionType = 'drag';
  });

  map.on('moveend', () => {
    const zoom = map.getView().getZoom();
    const center = map.getView().getCenter();
    if (zoom !== undefined && center) {
      localStorage.setItem('predictionMap.zoom', String(zoom));
      localStorage.setItem('predictionMap.center', String(center));
      if (shouldSkipMoveendAnalytics) {
        shouldSkipMoveendAnalytics = false;
        previousZoom = zoom;
        lastInteractionType = 'programmatic';
        return;
      }
      // GA4: 地図移動・ズーム操作の完了を計測
      sendAnalyticsEvent('map_interaction', {
        action: 'moveend',
        interaction_type: lastInteractionType,
        zoom_level: Math.round(zoom * 10) / 10,
        zoom_changed: previousZoom !== zoom,
      });
      previousZoom = zoom;
      lastInteractionType = 'programmatic';
    }
  });

  map.on('pointermove', e => {
    let isOnFeature = false;
    map.forEachFeatureAtPixel(e.pixel, (f): void => {
      // クラスタfeature
      const features = f.get('features');
      if (features && features.length === 1) {
        const hoveredFeature = features[0];
        if (hoveredFeature && hoveredFeature.get('title') && hoveredFeature.get('station')) {
          isOnFeature = true;
          onStationHover(hoveredFeature.get('station'));
          overlay.setPosition(e.coordinate);
        }
      }
    });
    if (!isOnFeature) {
      overlay.setPosition(undefined);
      onStationHover(null);
    }
  });

  map.on('singleclick', (e: MapBrowserEvent<any>) => {
    map.forEachFeatureAtPixel(e.pixel, (feature) => {
      // クラスタの場合はfeature.get('features')が配列
      const features = feature.get('features');
      if (features && features.length === 1) {
        onStationClick(features[0].get('index'));
        selectedPinSource.clear();
        selectedPinSource.addFeature(new Feature({
          geometry: features[0].get('geometry'),
        }));
      }
    });
  });

  viewport.style.cursor = 'pointer';

  return {
    map,
    baseLayers,
    alertSource,
    locationSource,
    pinSource,
    selectedPinSource,
    skipNextMoveendAnalytics: () => {
      shouldSkipMoveendAnalytics = true;
    },
    dispose: () => {
      viewport.removeEventListener('wheel', handleWheel);
      viewport.removeEventListener('touchstart', handleTouchStart);
      map.setTarget(undefined);
      map.dispose();
    },
  };
};

const PredictionMap = React.forwardRef<PredictionMapHandle, PredictionMapProps>(({ 
  latlng,
  style = {},
  alerts,
  goToCurrentLocation,
  pins = [],
  onClick,
  increment,
  setIncrement,
  isImageIdChecking,
  showFloodDepthControls = true,
}, ref) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const isMobile = useIsMobile();

  const mapRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  // Map生成後にstateへ入れ、依存するeffectを（宣言順に関係なく）生成後に必ず動かす
  const [resources, setResources] = useState<MapResources | null>(null);
  // 親から命令的に呼ばれる updateGeeUrl 用。stateの反映を待たずに参照できるようrefにも持つ
  const mapObjectRef = useRef<Map | null>(null);
  const layerRef = useRef<WebGLTile | null>(null);
  // 新しい浸水深レイヤーのタイルが届くまで残しておく旧レイヤー
  const retiringLayersRef = useRef<Set<WebGLTile>>(new Set());
  const currentGeeUrlRef = useRef('');
  const [hasFloodLayer, setHasFloodLayer] = useState(false);
  const [hoveredStation, setHoveredStation] = useState<Station | null>(null);
  const [isSettingMenu, setIsSettingMenu] = useState(false);
  // 浸水深レイヤーの透過率
  const [floodOpacity, setFloodOpacity] = useState(() => {
    const saved = localStorage.getItem('predictionMap.floodOpacity');
    return saved ? parseFloat(saved) : 0.7;
  });
  const floodOpacityRef = useRef(floodOpacity);

  // マップの種類（ダーク、航空、通常）の切り替えフォーム
  const [layerType, setLayerType] = useState(() => (
    normalizeLayerType(localStorage.getItem('predictionMap.layerType'))
  ));

  const selectedBaseLayerIndex = findBaseLayerIndex(normalizeLanguage(language), layerType);

  // Map生成時にだけ使う初期値。以降の変更は個別のeffectで反映する
  const initialLatlngRef = useRef(latlng);
  const initialBaseLayerIndexRef = useRef(selectedBaseLayerIndex);

  // イベントハンドラからは常に最新のonClickを呼ぶ
  const latestOnClickRef = useRef(onClick);
  useLayoutEffect(() => {
    latestOnClickRef.current = onClick;
  }, [onClick]);

  const applyGeeUrl = useCallback((mapObject: Map, nextGeeUrl: string): Promise<void> => {
    const currentLayer = layerRef.current;
    const retiringLayers = retiringLayersRef.current;

    if (!nextGeeUrl) {
      retiringLayers.forEach(layer => disposeLayer(mapObject, layer));
      retiringLayers.clear();
      if (currentLayer) {
        disposeLayer(mapObject, currentLayer);
        layerRef.current = null;
      }
      setHasFloodLayer(false);
      return Promise.resolve();
    }

    const source = new XYZ({
      url: nextGeeUrl,
    });

    // 読み込み中のタイルが0枚に戻ったら一段落とみなす。
    // キャッシュ済みでイベントが来ない場合は、呼び出し側の時間切れで先へ進む
    const tilesSettled = new Promise<void>(resolve => {
      let pending = 0;
      const handleTileDone = () => {
        pending -= 1;
        if (pending <= 0) {
          unByKey(settleKeys);
          resolve();
        }
      };
      const settleKeys = [
        source.on('tileloadstart', () => {
          pending += 1;
        }),
        source.on('tileloadend', handleTileDone),
        source.on('tileloaderror', handleTileDone),
      ];
    });

    const geelayer = new WebGLTile({
      style: colorStyle,
      source,
      maxZoom: undefined,
      zIndex: 2,
    });

    geelayer.setOpacity(floodOpacityRef.current);
    mapObject.addLayer(geelayer);

    if (currentLayer) {
      retiringLayers.add(currentLayer);
      // 新しいタイルが1枚届く（または失敗する）までは旧レイヤーを残してちらつきを防ぐ
      const layersToRemove = Array.from(retiringLayers);
      const removeRetiringLayers = () => {
        unByKey(keys);
        layersToRemove.forEach(layer => {
          if (retiringLayers.delete(layer)) {
            disposeLayer(mapObject, layer);
          }
        });
      };
      const keys = [
        source.once('tileloadend', removeRetiringLayers),
        source.once('tileloaderror', removeRetiringLayers),
      ];
    }

    layerRef.current = geelayer;
    setHasFloodLayer(true);
    return tilesSettled;
  }, []);

  // 透過率は ref から読むため、この関数は再生成されない
  const updateGeeUrl = useCallback((nextGeeUrl: string): Promise<void> => {
    const previousGeeUrl = currentGeeUrlRef.current;
    currentGeeUrlRef.current = nextGeeUrl;

    const mapObject = mapObjectRef.current;
    if (!mapObject) {
      // Map生成後に currentGeeUrlRef を適用する
      return Promise.resolve();
    }
    if (previousGeeUrl === nextGeeUrl && !!layerRef.current === !!nextGeeUrl) {
      return Promise.resolve();
    }

    return applyGeeUrl(mapObject, nextGeeUrl);
  }, [applyGeeUrl]);

  React.useImperativeHandle(ref, () => ({
    updateGeeUrl,
  }), [updateGeeUrl]);

  // Map本体はマウント中に1度だけ作る（StrictModeや再マウント時は作り直す）
  useEffect(() => {
    const target = mapRef.current;
    const popup = popupRef.current;
    if (!target || !popup) {
      return;
    }

    const created = createMapResources({
      target,
      popup,
      initialLatlng: initialLatlngRef.current,
      initialBaseLayerIndex: initialBaseLayerIndexRef.current,
      onStationClick: index => latestOnClickRef.current?.(index),
      // pointermoveごとに呼ばれる。同じStationならsetStateが再レンダリングを起こさない
      onStationHover: setHoveredStation,
    });
    mapObjectRef.current = created.map;
    setResources(created);

    if (currentGeeUrlRef.current) {
      applyGeeUrl(created.map, currentGeeUrlRef.current);
    }

    const retiringLayers = retiringLayersRef.current;
    return () => {
      retiringLayers.forEach(layer => layer.dispose());
      retiringLayers.clear();
      layerRef.current?.dispose();
      if (mapObjectRef.current === created.map) {
        mapObjectRef.current = null;
        layerRef.current = null;
      }
      created.dispose();
      setResources(null);
      setHasFloodLayer(false);
    };
  }, [applyGeeUrl]);

  useEffect(() => {
    if (!resources) {
      return;
    }
    const mapObject = resources.map;
    const zoomControl = new Zoom({ className: isMobile ? 'custom-map-zoom-mobile' : 'custom-map-zoom' });
    // 表示中の背景レイヤーの attributions を出す。位置はindex.cssで調整する
    const attributionControl = new Attribution({
      className: `ol-attribution custom-map-attribution${isMobile ? ' custom-map-attribution-mobile' : ''}`,
      collapsible: false,
    });
    mapObject.addControl(zoomControl);
    mapObject.addControl(attributionControl);
    return () => {
      mapObject.removeControl(zoomControl);
      mapObject.removeControl(attributionControl);
    };
  }, [resources, isMobile]);

  // 検索・現在地移動：中心と現在地マーカーだけを更新する（zoomは維持）
  // 親は移動のたびに新しい配列を渡す前提（同じ参照だとこのeffectは動かない）。
  // 値の比較は、すでに同じ座標にいるときの余計な setCenter を省くためだけのもの
  useEffect(() => {
    if (!resources) {
      return;
    }
    resources.locationSource.clear();
    if (!latlng) {
      return;
    }
    const currentLocationPin = drawPinFromLonlat(latlng);
    currentLocationPin.setStyle(currentIcon);
    resources.locationSource.addFeature(currentLocationPin);

    const coordinate = fromLonLat([latlng[1], latlng[0]]);
    const view = resources.map.getView();
    const center = view.getCenter();
    if (!center || center[0] !== coordinate[0] || center[1] !== coordinate[1]) {
      resources.skipNextMoveendAnalytics();
      view.setCenter(coordinate);
    }
  }, [resources, latlng]);

  // alertsに登録された地点の更新
  useEffect(() => {
    if (!resources) {
      return;
    }
    resources.alertSource.clear();
    resources.alertSource.addFeatures(createAlertFeatures(alerts));
  }, [resources, alerts]);

  // 観測所ピンの更新。Clusterは pinSource の変更に追従する
  useEffect(() => {
    if (!resources) {
      return;
    }
    resources.selectedPinSource.clear();
    resources.pinSource.clear();
    resources.pinSource.addFeatures(createStationFeatures(pins));
  }, [resources, pins]);

  // 浸水深レイヤーの透過率変更時にlocalStorageへ保存
  useEffect(() => {
    floodOpacityRef.current = floodOpacity;
    localStorage.setItem('predictionMap.floodOpacity', String(floodOpacity));
    if (layerRef.current) {
      layerRef.current.setOpacity(floodOpacity);
    }
  }, [floodOpacity]);

  // 言語・地図の種類が変わったら、選択中の背景だけを表示対象にする
  useEffect(() => {
    resources?.baseLayers.forEach((layer, i) => {
      layer.setVisible(i === selectedBaseLayerIndex);
    });
  }, [resources, selectedBaseLayerIndex]);

  // 選択状態を保存
  useEffect(() => {
    localStorage.setItem('predictionMap.layerType', layerType);
  }, [layerType]);

  return (
    <Box position="relative">
      <div ref={mapRef} style={{ width: '100%', height: 'calc(100vh - 60px)', ...{ ...style } }} />

      {showFloodDepthControls && (
        <Stack
          position="absolute"
          zIndex={3}
          sx={isMobile ? { bottom: 290, right: 16 } : { bottom: 138, left: 24 }}
          spacing={2}
          alignItems={isMobile ? 'flex-end' : 'flex-start'}
        >
          <FloodDepthMeter />
        </Stack>
      )}
      { 0 < increment && 
      <Box
        sx={{
          position: 'absolute',
          zIndex: 2,
          border: '1px solid #ccc',
          backgroundColor: '#fff',
          padding: '8px 16px',
          ...(isMobile
            ? { width: 'calc(100% - 8px)', maxWidth: '400px', height: '170px', margin: '0 auto', left: 0, right: 0, bottom: 4 }
            : { width: 320, top: 132, left: 24 }
          )
        }}
      >
        <TimeSelectSlider
          setIncrement={setIncrement}
          increment={increment}
          isImageIdChecking={isImageIdChecking}
        />
      </Box>
      }

      {showFloodDepthControls && (!isSettingMenu ? (
        <Stack sx={{
          position: 'absolute',
          zIndex: 3,
          width: 40,
          height: 40,
          backgroundColor: 'rgba(0, 0, 0, 0.54)',
          borderRadius: 2,
          ...(isMobile
            ? { bottom: 232, right: 24, }
            : { bottom: 76, left: 24 }
          )
        }}>
          <IconButton onClick={() => {
            // GA4: 地図設定メニューを開いた操作を計測
            sendAnalyticsEvent('map_settings', { action: 'open' });
            setIsSettingMenu(true);
          }}>
            <SettingsIcon sx={{ color: '#fff' }} />
          </IconButton>
        </Stack>
      ) : (
        <Stack
          sx={{
            position: 'absolute',
            overflow: 'visible',
            width: 300,
            zIndex: 3,
            backgroundColor: 'rgba(0, 0, 0, 0.54)',
            padding: '8px 24px 16px',
            borderRadius: 2,
            boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            ...(isMobile
              ? { bottom: 180, left: 0, right: 0, margin: '0 auto', }
              : { bottom: 24, left: 24 }
            )
          }}
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={2}
        >
          <SliderBox target={hasFloodLayer ? layerRef.current : null} initialValue={floodOpacity} onOpacityChange={setFloodOpacity} />

          <Stack>
            <Typography sx={{ color: '#fff', textShadow: '0 1px 2px #333', fontWeight: 'bold', fontSize: 12, mb: 1 }}>{t('', '地図の選択')}</Typography>
            <Select
              name="layerType"
              value={layerType}
              size="small"
              sx={{
                backgroundColor: '#fff',
              }}
              onChange={(e) => {
                // GA4: 地図種別の変更を計測
                sendAnalyticsEvent('map_settings', {
                  action: 'change_layer_type',
                  layer_type: e.target.value,
                });
                setLayerType(e.target.value);
              }}
                  // GA4: 地図設定メニューを閉じた操作を計測
            >
              {MAPBOX_LAYER_TYPE_SELECT_MENUITEMS.map((d) => (
                <MenuItem key={d.label} value={d.value}>
                  {t(d.label)}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <IconButton
            onClick={() => {
              sendAnalyticsEvent('map_settings', { action: 'close' });
              setIsSettingMenu(false);
            }}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 22,
              height: 22,
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.72)',
              backgroundColor: 'rgba(255,255,255,0.08)',
              p: 0,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.16)',
              },
            }}
            aria-label="close-slider-menu"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      ))}
      
      <Box
        sx={{
          position: 'absolute',
          backgroundColor: '#fff',
          padding: '16px',
          border: '1px solid #ccc',
          left: '-50px',
          bottom: '12px',
          minWidth: '140px',
          '&:after': {
            top: '100%',
            border: 'solid transparent',
            content: '" "',
            height: 0,
            width: 0,
            position: 'absolute',
            pointerEvents: 'none',
            borderTopColor: 'white',
            borderWidth: '10px',
            left: '48px',
            marginLeft: '-10px',
          },
          '&:before': {
            top: '100%',
            border: 'solid transparent',
            content: '" "',
            height: 0,
            width: 0,
            position: 'absolute',
            pointerEvents: 'none',
            borderTopColor: '#cccccc',
            borderWidth: '11px',
            left: '48px',
            marginLeft: '-11px',
          },
        }}
        ref={popupRef}
      >
        {hoveredStation && (
          <Stack>
            <Typography>水系名：{hoveredStation.basin_name}</Typography>
            <Typography>河川名：{hoveredStation.river_name}</Typography>
            <Typography>地点名：{hoveredStation.station_name}</Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
});

const areStationsEqual = (prevPins: Station[] = [], nextPins: Station[] = []) => {
  if (prevPins === nextPins) {
    return true;
  }
  if (prevPins.length !== nextPins.length) {
    return false;
  }
  for (let i = 0; i < prevPins.length; i += 1) {
    if (prevPins[i]?.id !== nextPins[i]?.id) {
      return false;
    }
  }
  return true;
};

const MemoizedPredictionMap = React.memo(PredictionMap, (prev, next) => {
  return (
    prev.latlng === next.latlng
    && prev.style === next.style
    && prev.alerts === next.alerts
    && prev.goToCurrentLocation === next.goToCurrentLocation
    && prev.onClick === next.onClick
    && prev.increment === next.increment
    && prev.setIncrement === next.setIncrement
    && prev.isImageIdChecking === next.isImageIdChecking
    && prev.showFloodDepthControls === next.showFloodDepthControls
    && areStationsEqual(prev.pins, next.pins) 
  );
});

export default MemoizedPredictionMap;
